import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { fetchData, deleteData, updateData } from "../../services/apiServices";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import classnames from "classnames";

const Category = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [isReordering, setIsReordering] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await deleteData(`api/admin/deletecategories/${id}`);
        toast.success("Category deleted successfully");
        getData(); // Refresh the list
      } catch (error) {
        toast.error("Failed to delete category");
      }
    }
  };

  // Bulk update categories order
  const handleBulkOrderUpdate = async (updatedCategories) => {
    setIsReordering(true);
    try {
      const updatePromises = updatedCategories.map((category, index) =>
        updateData(`api/admin/categories/${category._id}`, { order: index + 1 })
      );

      await Promise.all(updatePromises);
      toast.success("Categories reordered successfully");
      getData(); // Refresh data
    } catch (error) {
      console.error("Failed to update order:", error);
      toast.error("Failed to update categories order");
    } finally {
      setIsReordering(false);
    }
  };

  // Handle drag end for categories
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(rows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setRows(items);
    handleBulkOrderUpdate(items);
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Order</span>,
      selector: (row) => row.order || 0,
      sortable: true,
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        <img
          src={row.image}
          alt={row.name}
          style={{ width: "50px", height: "50px", objectFit: "cover" }}
        />
      ),
      width: "100px",
    },
    {
      name: <span className="font-weight-bold fs-13">Title</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Description</span>,
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Created At</span>,
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Actions</span>,
      cell: (row) => (
        <div>
          <Link
            to={`/categories/edit/${row._id}`}
            className="btn btn-primary btn-sm me-2"
          >
            Edit
          </Link>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const getData = async () => {
    try {
      const response = await fetchData("api/admin/categories");

      if (response && response.data && response.data.categories) {
        // Sort by order field (ascending)
        const sortedCategories = response.data.categories.sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );
        setRows(sortedCategories);
      } else {
        setRows([]);
      }

      setPending(false);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError(error.message);
      toast.error("Failed to load categories");
      setPending(false);
    }
  };

  useEffect(() => {
    getData();
    const timeout = setTimeout(() => {
      setPending(false);
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Drag and drop item component
  const DragDropItem = ({ category, index }) => (
    <Draggable key={category._id} draggableId={category._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`card mb-2 ${snapshot.isDragging ? "shadow-lg" : ""}`}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <div className="card-body p-3">
            <Row className="align-items-center">
              <Col xs="1">
                <div className="text-muted">
                  <i className="mdi mdi-drag-horizontal fs-16"></i>
                </div>
              </Col>
              <Col xs="1">
                <span className="badge bg-primary">
                  {category.order || index + 1}
                </span>
              </Col>
              <Col xs="2">
                <img
                  src={category.image}
                  alt={category.name}
                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                  className="rounded"
                />
              </Col>
              <Col xs="3">
                <h6 className="mb-1">{category.name}</h6>
              </Col>
              <Col xs="3">
                <p className="text-muted mb-0 small">{category.description}</p>
              </Col>
              <Col xs="2">
                <div className="d-flex gap-1">
                  <Link
                    to={`/categories/edit/${category._id}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="mdi mdi-pencil"></i>
                  </Link>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(category._id)}
                  >
                    <i className="mdi mdi-delete"></i>
                  </button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div>
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-table me-2"></i>
                    Table View
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-drag me-2"></i>
                    Reorder Categories
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>
          </Row>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <DataTable
                columns={columns}
                pagination
                data={rows}
                progressPending={pending}
                defaultSortFieldId={1}
                defaultSortAsc={true}
              />
            </TabPane>

            <TabPane tabId="2">
              <Row className="mb-3">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Drag and Drop to Reorder Categories
                    </h5>
                    {isReordering && (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="me-2" />
                        <span>Updating order...</span>
                      </div>
                    )}
                  </div>
                  <p className="text-muted small">
                    Drag categories up or down to change their display order.
                    Changes are saved automatically.
                  </p>
                </Col>
              </Row>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="categories">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`${
                        snapshot.isDraggingOver ? "bg-light" : ""
                      } p-2 rounded`}
                      style={{ minHeight: "200px" }}
                    >
                      {rows.map((category, index) => (
                        <DragDropItem
                          key={category._id}
                          category={category}
                          index={index}
                        />
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default Category;
